import { PanoMeasurementToolState } from "@/store/measurement-tool-slice";
import { useEffect, useRef, useState, type JSX } from "react";
import {
  CircleGeometry,
  DoubleSide,
  Group,
  Mesh,
  MeshBasicMaterial,
  Object3D,
  Vector3,
} from "three";
import { PREVIEW_FEATURE_NAME } from "../preview-feature-renderer";
import { PanoMeasureWallRenderer } from "./pano-measure-wall-renderer";

type PanoMeasureToolRendererProps = {
  /** The first point on the floor of the wall*/
  floorPoint1: Vector3 | undefined;

  /** The second point on the floor of the wall*/
  floorPoint2: Vector3 | undefined;

  /** The point on the ceiling of the wall*/
  ceilingPoint: Vector3 | undefined;

  /** The current state of the pano measurement tool */
  state: PanoMeasurementToolState;
};

/**
 * @returns The renderer of the pano measurement tool
 */
export function PanoMeasureToolRenderer({
  floorPoint1,
  floorPoint2,
  ceilingPoint,
  state,
}: PanoMeasureToolRendererProps): JSX.Element | null {
  switch (state) {
    case PanoMeasurementToolState.SelectFloorPoint1:
      return <HoverPointRenderer position={floorPoint1} />;
    case PanoMeasurementToolState.SelectFloorPoint2:
      return (
        <group>
          <PickedPointRenderer position={floorPoint1} />
          <HoverPointRenderer position={floorPoint2} />
        </group>
      );
    case PanoMeasurementToolState.SelectCeilingPoint:
      return (
        <group>
          <PickedPointRenderer position={floorPoint1} />
          <PickedPointRenderer position={floorPoint2} />
          {floorPoint1 && floorPoint2 && ceilingPoint && (
            <PanoMeasureWallRenderer
              floorPoint1={floorPoint1}
              floorPoint2={floorPoint2}
              height={ceilingPoint.y - floorPoint1.y}
            />
          )}
        </group>
      );
  }
  // no visual feedback needed
  return null;
}

// cached temporary objects for avoiding repleated allocation
const Z_AXIS = new Vector3(0, 0, 1);
const Y_AXIS = new Vector3(0, 1, 0);

const DISC_RADIUS = 0.3;

// Renderer for the hover point as a disc on the floor
function HoverPointRenderer({
  position,
}: {
  position: Vector3 | undefined;
}): JSX.Element | null {
  const [discObj] = useState<Object3D>(() => {
    const discGeom = new CircleGeometry(DISC_RADIUS);
    const disc = new Mesh(
      discGeom,
      new MeshBasicMaterial({
        color: "white",
        transparent: true,
        opacity: 0.5,
        side: DoubleSide,
      }),
    );
    disc.name = PREVIEW_FEATURE_NAME;

    const group = new Group();
    group.add(disc);
    group.quaternion.setFromUnitVectors(Z_AXIS, Y_AXIS);
    return group;
  });
  const discRef = useRef<Group>(null);

  useEffect(() => {
    if (discRef.current && position) {
      discRef.current.position.copy(position);
    }
  }, [position, discRef]);

  if (!position) return null;

  return (
    <group ref={discRef}>
      <primitive object={discObj} />
    </group>
  );
}

const POINT_SIZE = 0.04;

// Renderer for picked point as a small semi-transparent sphere
function PickedPointRenderer({
  position,
}: {
  position: Vector3 | undefined;
}): JSX.Element | null {
  if (!position) return null;
  return (
    <mesh position={position}>
      <sphereGeometry args={[POINT_SIZE]} />
      <meshBasicMaterial
        color="blue"
        transparent
        opacity={0.6}
        depthTest={false}
        side={DoubleSide}
      />
    </mesh>
  );
}
